import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { removeAllFeatures } from "@mapsight/core/lib/feature-sources/actions";
import { createFilteredFeatureSourceSelector } from "@mapsight/core/lib/feature-sources/selectors";
import { FEATURE_SOURCES, MAP } from "../../config/constants/controllers";
import { translate } from "../../helpers/i18n";
import { createActivateAction, createDeactivateAction, } from "../../plugins/browser/share-position-link";
import useControllableState from "../../hooks/useControllableState";
import LinkShare from "../link-share";
import ToolOverlay from "./tool-overlay";
const applyPrecisionToCoordinates = (num) => Number.parseFloat(num).toPrecision(6);
const SharePositionLinkButton = ({ pluginName = "sharePositionLink", opened, onOpenedChange, }) => {
    var _a;
    const [isOpened = false, setIsOpened] = useControllableState({
        prop: opened,
        defaultProp: false,
        onChange: onOpenedChange,
    });
    const activate = useMemo(() => createActivateAction(MAP, pluginName), [pluginName]);
    const deactivate = useMemo(() => createDeactivateAction(MAP, pluginName), [pluginName]);
    const resetAction = useMemo(() => removeAllFeatures(FEATURE_SOURCES, `${pluginName}_featureSource`), [pluginName]);
    const featureSelector = useMemo(() => createSelector(createFilteredFeatureSourceSelector(FEATURE_SOURCES, `${pluginName}_featureSource`, MAP), (featureSourceState) => { var _a, _b; return (_b = (_a = featureSourceState === null || featureSourceState === void 0 ? void 0 : featureSourceState.data) === null || _a === void 0 ? void 0 : _a.features) === null || _b === void 0 ? void 0 : _b[0]; }), [pluginName]);
    const dispatch = useDispatch();
    const feature = useSelector(featureSelector);
    const coords = ((_a = feature === null || feature === void 0 ? void 0 : feature.geometry) === null || _a === void 0 ? void 0 : _a.type) === "Point"
        ? feature.geometry.coordinates
        : undefined;
    const shareUrl = useMemo(() => {
        if (coords) {
            const url = new URL(location.href);
            url.protocol = "https:";
            url.hash = `#lm=${applyPrecisionToCoordinates(coords[1])}/${applyPrecisionToCoordinates(coords[0])}`;
            return url.href;
        }
        return null;
    }, [coords]);
    const toggle = useCallback(() => {
        setIsOpened(!isOpened);
    }, [isOpened, setIsOpened]);
    const close = useCallback(() => {
        setIsOpened(false);
    }, [setIsOpened]);
    useEffect(() => {
        dispatch(isOpened ? activate : deactivate);
    }, [dispatch, deactivate, isOpened, activate]);
    const closeAndReset = useCallback(function () {
        dispatch(resetAction);
        close();
    }, [close, dispatch, resetAction]);
    return (_jsxs(Fragment, { children: [_jsx("button", { className: "ms3-map-overlay__button ms3-map-overlay__button--with-icon ms3-map-overlay__button--share-link", type: "button", onClick: toggle, children: _jsx("span", { className: "ms3-map-overlay__button__label", children: translate("ui.share-position-link.title") }) }), isOpened && (_jsx(ToolOverlay, { label: translate("ui.share-position-link.title"), onClose: closeAndReset, text: translate("ui.share-position-link.instructions"), children: feature && (_jsx(LinkShare, { url: shareUrl, title: translate("ui.share-position-link.shareTitle"), buttonLabel: translate("ui.share-position-link.shareButtonLabel"), onFinished: close, onError: undefined })) }))] }));
};
export default SharePositionLinkButton;
